import { useState, useCallback } from "react";
import type { ShareTemplate } from "../types/share-template.types";
import apiService from "../../../../service/Api";

interface UseShareTemplatesReturn {
  templates: ShareTemplate[];
  loading: boolean;
  error: string | null;
  fetchTemplates: (category?: string) => Promise<void>;
  fetchTemplatesByCategory: (category: string) => Promise<void>;
  saveTemplate: (
    template: Omit<ShareTemplate, "id" | "createdAt">
  ) => Promise<ShareTemplate>;
  updateTemplate: (
    id: string,
    template: Partial<ShareTemplate>
  ) => Promise<void>;
  deleteTemplate: (id: string) => Promise<void>;
}

export const useShareTemplates = (): UseShareTemplatesReturn => {
  const [templates, setTemplates] = useState<ShareTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTemplates = useCallback(async (category?: string) => {
    setLoading(true);
    setError(null);

    try {
      const url = category
        ? `/api/configurations/message-templates/?category=funeral`
        : "/api/configurations/message-templates/";

      const response = await apiService.get<ShareTemplate[]>(url);
      setTemplates(response.data);
    } catch (err: unknown) {
      console.error("[v0] Template fetch error:", err);
      setError((err as { message?: string })?.message || "Failed to fetch templates");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTemplatesByCategory = useCallback(
    async (category: string) => {
      await fetchTemplates(category);
    },
    [fetchTemplates]
  );

  const saveTemplate = useCallback(
    async (
      template: Omit<ShareTemplate, "id" | "createdAt">
    ): Promise<ShareTemplate> => {
      try {
        const { data } = await apiService.post<ShareTemplate>(
          "/api/configurations/message-templates/",
          template
        );
        setTemplates((prev) => [...prev, data]);
        return data;
      } catch (err: unknown) {
        setError((err as { message?: string })?.message || "Failed to save template");
        throw err;
      }
    },
    []
  );

  const updateTemplate = useCallback(
    async (id: string, updates: Partial<ShareTemplate>) => {
      try {
        const { data } = await apiService.put<ShareTemplate>(
          `/api/configurations/message-templates/${id}/`,
          updates
        );
        setTemplates((prev) => prev.map((t) => (t.id === id ? data : t)));
      } catch (err: unknown) {
        setError((err as { message?: string })?.message || "Failed to update template");
        throw err;
      }
    },
    []
  );

  const deleteTemplate = useCallback(async (id: string) => {
    try {
      await apiService.delete(`/api/configurations/message-templates/${id}/`);
      setTemplates((prev) => prev.filter((t) => t.id !== id));
    } catch (err: unknown) {
      setError((err as { message?: string })?.message || "Failed to delete template");
      throw err;
    }
  }, []);

  return {
    templates,
    loading,
    error,
    fetchTemplates,
    fetchTemplatesByCategory,
    saveTemplate,
    updateTemplate,
    deleteTemplate,
  };
};
